import { useState, useCallback, useEffect, useLayoutEffect, useMemo, RefCallback } from "react";
import { usePopper } from 'react-popper';
import { useFocusVisible } from "react-aria";
import { isUsingTouchscreen } from "../../utils/pointer";

type Placement = 
    | 'auto' 
    | 'auto-start' 
    | 'auto-end' 
    | 'top' 
    | 'top-start' 
    | 'top-end' 
    | 'bottom' 
    | 'bottom-start' 
    | 'bottom-end' 
    | 'right' 
    | 'right-start' 
    | 'right-end' 
    | 'left' 
    | 'left-start' 
    | 'left-end';

type PopperPlacement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Config to pass to usePopper. See https://popper.js.org/docs/v2/constructors for more info.
 */
interface Config {
    /** Determines which usePopper positioning strategy to use ('fixed' or 'absolute'). */
    fixedPositioning?: boolean;
    /** Placement of popup. */
    placement?: Placement;
    /** Whether trigger element is disabled. */
    disabled?: boolean;
    /** Children of tooltip. Pass this in if children can change whilst tooltip is open, and hook will recalculate when that happens. If `children` is a node, ensure it is memoized. */
    children?: string | React.ReactNode;
    /** Maximum width of tooltip. */
    maxWidth?: string;
    /** Whether to show tooltip on mobile/touchscreen devices. */
    showOnMobile?: boolean;
    /** Delay in milliseconds before showing tooltip on hover. Default is 0 (no delay). */
    delay?: number;
}

interface ArrowProps {
    /** Callback ref for arrow. */
    ref: RefCallback<HTMLDivElement>;
    /** Styles to apply to arrow. */
    style: React.CSSProperties;
    /** Size of arrow in px. */
    size: number;
}

interface TooltipProps {
    /** Is tooltip showing? */
    show: boolean;
    /** Callback ref for tooltip. */
    ref: RefCallback<HTMLDivElement>;
    /** Styles to apply to tooltip. */
    style: React.CSSProperties;
    /** Placement of tooltip. */
    placement?: PopperPlacement;
    /** Props for arrow. */
    arrowProps: ArrowProps;
    /** Maximum width of tooltip. */
    maxWidth?: string;
}

interface TriggerElementProps {
    /** Function called when mouse hovers over element. */
    onMouseEnter: () => void;
    /** Function called when mouse stops hovering over element. */
    onMouseLeave: () => void;
    /** Function called when element gains focus. */
    onFocus: () => void;
    /** Function called when element loses focus. */
    onBlur: () => void;
    /** Callback ref for element. */
    ref: RefCallback<HTMLElement>;
    /** Disable element. */
    disabled?: boolean;
}

type Update = (() => Promise<Partial<any>>) | null;

/**
 * Custom hook for managing tooltip state and props.
 * Returns props to pass to the Tooltip component and to the component which triggers the tooltip on hover.
 * 
 * This does not currently support interactable tooltips (i.e. tooltips that contain clickable elements) as it will close
 * the moment the user is no longer hovering over trigger element. To support this case, we just need to add another state
 * `hoverTooltip` (similar to `hoverTriggerElement`) and show the tooltip if either the trigger element OR the tooltip is
 * being hovered over. Would probably need to hide it on a timeout as well as user will be briefly hovering over neither
 * element whilst they move from trigger to tooltip.
 * 
 * @param config Config to pass to usePopper.
 * @returns Array containing tooltip props, trigger element props, and update function
 */
const useTooltip = ({
    fixedPositioning = false,
    placement = 'top',
    disabled,
    maxWidth,
    showOnMobile = true,
    children,
    delay = 0
}: Config = {}): [TooltipProps, TriggerElementProps, Update] => {

    const { isFocusVisible } = useFocusVisible();

    // Whether trigger element is being hovered over
    const [hoverTriggerElement, setHoverTriggerElement] = useState(false);

    // Delayed hover state (used when delay > 0)
    const [delayedHover, setDelayedHover] = useState(false);

    // Whether trigger element is in focus
    const [focusTriggerElement, setFocusTriggerElement] = useState(false);

    // Handle delayed hover effect
    useEffect(() => {
        if (delay > 0) {
            if (hoverTriggerElement) {
                const timeoutId = setTimeout(() => {
                    setDelayedHover(true);
                }, delay);
                return () => clearTimeout(timeoutId);
            } else {
                setDelayedHover(false);
            }
        }
    }, [hoverTriggerElement, delay]);

    // Make sure tooltip is hidden if trigger element becomes disabled
    // If user was hovering over element at the point it became disabled, onMouseExit will not get called
    useEffect(() => {
        if (disabled) {
            setHoverTriggerElement(false);
            setFocusTriggerElement(false);
            setDelayedHover(false);
        }
    }, [disabled]);

    // MouseEnter function for trigger element
    const onTriggerElementMouseEnter = useCallback(() => setHoverTriggerElement(true), []);

    // MouseExit function for trigger element
    const onTriggerElementMouseLeave = useCallback(() => setHoverTriggerElement(false), []);

    // onFocus function for trigger element
    const onTriggerElementFocus = useCallback(() => setFocusTriggerElement(true), []);

    // onBlur function for trigger element
    const onTriggerElementBlur = useCallback(() => setFocusTriggerElement(false), []);

    // Callback refs for trigger element, tooltip container and arrow
    const [triggerElement, _setTriggerElement] = useState<HTMLElement | null>(null);
    const setTriggerElement = useCallback((element: HTMLElement | null) => {
        // Make sure state gets reset when element is removed
        if (!element) {
            setHoverTriggerElement(false);
            setFocusTriggerElement(false);
        }
        _setTriggerElement(element);
    }, []);
    const [tooltipContainerElement, setTooltipContainerElement] = useState<HTMLDivElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

    // usePopper handles positioning the tooltip
    const { styles, attributes, update } = usePopper(triggerElement, tooltipContainerElement, {
        modifiers: [
            { 
                name: 'arrow',
                options: {
                    element: arrowElement,
                    // Allow for rounded borders of tooltip
                    padding: 6
                }
            },
            {
                name: "offset",
                options: {
                    offset: [0, ARROW_SIZE]
                }
            },
            
            // Prevent tooltip from touching screen edge
            {
                name: 'preventOverflow',
                options: {
                    padding: 20
                },
            }
        ],
        placement,
        strategy: fixedPositioning ? 'fixed' : 'absolute'
    });

    // Need to recalculate tooltip size and position if children changes whilst it's open
    useLayoutEffect(() => {
        if (children && update) {
            update();
        }
    }, [children, update]);

    const triggerElementProps = useMemo(() => {
        return {
            onMouseEnter: onTriggerElementMouseEnter,
            onMouseLeave: onTriggerElementMouseLeave,
            onFocus: onTriggerElementFocus,
            onBlur: onTriggerElementBlur,
            ref: setTriggerElement,
            disabled
        };
    }, [onTriggerElementMouseEnter, onTriggerElementMouseLeave, onTriggerElementFocus, onTriggerElementBlur, setTriggerElement, disabled]);

    // Determine if tooltip should show based on hover state (with or without delay)
    const isHovering = delay > 0 ? delayedHover : hoverTriggerElement;

    return [
        // Tooltip props
        {
            // Show tooltip if mouse is hovering over it (respecting delay)
            // OR keyboard user has focused it
            show: (!showOnMobile && isUsingTouchscreen()) ? false : isHovering || (focusTriggerElement && isFocusVisible),
            ref: setTooltipContainerElement,
            style: styles.popper,
            placement: attributes?.popper?.['data-popper-placement'] as PopperPlacement,
            arrowProps: {
                ref: setArrowElement,
                style: styles.arrow,
                size: ARROW_SIZE
            },
            maxWidth
        },
        triggerElementProps,
        update
    ];
};

// In px
const ARROW_SIZE = 8;

export default useTooltip;